import React, { useState, useEffect } from 'react';
import {
  FaSearch, FaBullhorn, FaChartLine, FaLaptopCode, FaUserTie, FaCertificate,
  FaGraduationCap, FaHandshake, FaLightbulb, FaRocket, FaCheckCircle,
  FaArrowRight, FaStar, FaAward, FaChevronRight, FaBook, FaCog,
  FaUsers, FaGlobe, FaShieldAlt, FaBolt
} from 'react-icons/fa';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { Link } from 'react-router';

const OurServices = () => {
  const [activeService, setActiveService] = useState(0);
  const [counts, setCounts] = useState({ formations: 0, students: 0, satisfaction: 0, partners: 0 });
  const [visibleSections, setVisibleSections] = useState(new Set(['hero'])); // Ajouter 'hero' dès le départ
  const [activeAccordion, setActiveAccordion] = useState(0);

  // Intersection Observer pour animations au scroll
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px' // Réduction de la marge pour une détection plus précoce
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisibleSections(prev => new Set([...prev, entry.target.dataset.section]));
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    
    // Petit délai pour s'assurer que le DOM est prêt
    const timer = setTimeout(() => {
      const sections = document.querySelectorAll('[data-section]');
      sections.forEach(section => {
        observer.observe(section);
        // Vérifier immédiatement si la section est visible
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          setVisibleSections(prev => new Set([...prev, section.dataset.section]));
        }
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  // Animation des compteurs
  useEffect(() => {
    const targets = { formations: 500, students: 10000, satisfaction: 95, partners: 150 };
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    const increment = {
      formations: targets.formations / steps,
      students: targets.students / steps,
      satisfaction: targets.satisfaction / steps,
      partners: targets.partners / steps
    };

    let currentStep = 0;
    const timer = setInterval(() => {
      if (currentStep < steps) {
        setCounts({
          formations: Math.min(Math.floor(increment.formations * currentStep), targets.formations),
          students: Math.min(Math.floor(increment.students * currentStep), targets.students),
          satisfaction: Math.min(Math.floor(increment.satisfaction * currentStep), targets.satisfaction),
          partners: Math.min(Math.floor(increment.partners * currentStep), targets.partners)
        });
        currentStep++;
      } else {
        clearInterval(timer);
        setCounts(targets);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const mainServices = [
    {
      icon: FaBullhorn,
      title: 'Formations Sur Mesure',
      description: 'Programmes de formation personnalisés adaptés précisément aux besoins stratégiques de votre entreprise.',
      color: '#fd7e14',
      features: ['Analyse des besoins', 'Programmes personnalisés', 'Suivi post-formation'],
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&q=80'
    },
    {
      icon: FaCertificate,
      title: 'Certifications Internationales',
      description: 'Obtenez des certifications reconnues mondialement avec nos formations accréditées PECB.',
      color: '#198754',
      features: ['Certifications PECB', 'Reconnues internationalement', 'Validation des acquis'],
      image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=600&q=80'
    },
    {
      icon: FaLaptopCode,
      title: 'E-Learning Avancé',
      description: 'Plateforme d\'apprentissage moderne accessible 24/7 avec contenus interactifs et suivis personnalisés.',
      color: '#8b5cf6',
      features: ['Accès 24/7', 'Contenus interactifs', 'Certification en ligne'],
      image: 'https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=600&q=80'
    },
    {
      icon: FaChartLine,
      title: 'Conseil & Audit RH',
      description: 'Expertise en gestion des ressources humaines et optimisation des processus organisationnels.',
      color: '#dc3545',
      features: ['Diagnostic RH', 'Stratégie de recrutement', 'Optimisation des processus'],
      image: 'https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=600&q=80'
    }
  ];

  const additionalServices = [
    { icon: FaUserTie, title: 'Recrutement Experts', description: 'Identification et recrutement des meilleurs talents pour votre entreprise.', color: '#fd7e14' },
    { icon: FaGraduationCap, title: 'Formation Continue', description: 'Programmes de développement professionnel continu pour vos équipes.', color: '#198754' },
    { icon: FaHandshake, title: 'Coaching Pro', description: 'Accompagnement personnalisé pour le développement de carrière.', color: '#8b5cf6' },
    { icon: FaLightbulb, title: 'Innovation', description: 'Ateliers créatifs pour stimuler l\'innovation dans vos équipes.', color: '#20c997' },
    { icon: FaRocket, title: 'Startup Support', description: 'Support dédié aux startups pour structurer leurs équipes.', color: '#fbbf24' },
    { icon: FaGlobe, title: 'International', description: 'Formations disponibles dans 6 pays africains et en France.', color: '#06b6d4' }
  ];

  const processSteps = [
    { number: '01', title: 'Consultation Gratuite', description: 'Échangeons sur vos besoins et objectifs de formation', icon: FaSearch },
    { number: '02', title: 'Analyse & Proposition', description: 'Nos experts analysent et proposent un programme adapté', icon: FaBook },
    { number: '03', title: 'Mise en Œuvre', description: 'Déploiement de la formation avec suivi qualité', icon: FaCog },
    { number: '04', title: 'Suivi & Certification', description: 'Évaluation des acquis et délivrance des certifications', icon: FaAward }
  ];

  const testimonials = [
    { name: 'Marie Kouassi', role: 'DRH, Tech Solutions', avatar: 'https://i.pravatar.cc/150?img=1', text: 'CINEF a transformé notre équipe avec des formations de qualité exceptionnelle. Nos collaborateurs sont montés en compétences rapidement.', rating: 5, company: 'Tech Solutions' },
    { name: 'Jean-Paul Mensah', role: 'CEO, Finance Group', avatar: 'https://i.pravatar.cc/150?img=3', text: 'Un partenaire de confiance depuis 3 ans. L\'accompagnement est personnalisé et les résultats sont mesurables.', rating: 5, company: 'Finance Group' },
    { name: 'Aïcha Diallo', role: 'Chef de Projet', avatar: 'https://i.pravatar.cc/150?img=5', text: 'Ma certification PECB obtenue avec CINEF a été un vrai tournant dans ma carrière. Je recommande vivement !', rating: 5, company: 'Consulting Pro' }
  ];

  const faqs = [
    { question: 'Quels types de formations proposez-vous ?', answer: 'Nous proposons des formations en présentiel, e-learning et blended learning dans 11 domaines d\'expertise : juridique, finance, RH, audit, marketing, IT, etc.' },
    { question: 'Les certifications sont-elles reconnues ?', answer: 'Oui, nos certifications sont internationalement reconnues, notamment les certifications PECB et nos Certificats de Compétences CINEF.' },
    { question: 'Proposez-vous des formations sur mesure ?', answer: 'Absolument ! Nous créons des programmes personnalisés adaptés aux besoins spécifiques de votre entreprise.' },
    { question: 'Quel est le délai pour démarrer une formation ?', answer: 'Selon vos besoins, nous pouvons démarrer une formation en aussi peu que 2 semaines après la consultation initiale.' }
  ];

  return (
    <>
      <Header />
      <div className="services-wrapper">
        {/* Hero Section */}
        <section className="services-hero visible" data-section="hero">
          <div className="hero-background">
            <div className="hero-shape shape-1"></div>
            <div className="hero-shape shape-2"></div>
            <div className="hero-shape shape-3"></div>
          </div>

          <div className="services-container">
            <div className="hero-content">
              <div className="hero-left">
                <div className="hero-badge">
                  <FaAward className="badge-icon-rotate" />
                  <span>Excellence en Formation Professionnelle</span>
                </div>

                <h1 className="hero-title">
                  Des solutions innovantes <br />
                  pour <span className="gradient-text">votre réussite</span>
                </h1>

                <p className="hero-description">
                  Transformez vos ambitions en compétences avec nos programmes de formation
                  professionnelle reconnus dans 6 pays. Plus de 10 000 professionnels nous font confiance.
                </p>

                <div className="hero-buttons">
                  {/* <Link to="/services" className="btn-primary-hero">
                    Découvrir Nos Services
                    <FaArrowRight />
                  </Link> */}

                  <Link to="/ledevis" className="btn-secondary-hero">
                    
                    Demander un Devis
                  </Link>
                </div>


                <div className="hero-stats">
                  <div className="hero-stat">
                    <div className="stat-value">{counts.formations}+</div>
                    <div className="stat-label">Formations</div>
                  </div>
                  <div className="hero-stat">
                    <div className="stat-value">{counts.students}+</div>
                    <div className="stat-label">Apprenants</div>
                  </div>
                  <div className="hero-stat">
                    <div className="stat-value">{counts.satisfaction}%</div>
                    <div className="stat-label">Satisfaction</div>
                  </div>
                </div>
              </div>

              <div className="hero-right">
                <div className="hero-image-container">
                  <img
                    src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
                    alt="Équipe professionnelle"
                    className="hero-main-image"
                  />
                  <div className="floating-card card-1">
                    <FaUsers className="float-icon" />
                    <div className="float-content">
                      <div className="float-number">{counts.partners}+</div>
                      <div className="float-text">Partenaires</div>
                    </div>
                  </div>
                  <div className="floating-card card-2">
                    <FaAward className="float-icon" />
                    <div className="float-content">
                      <div className="float-number">15+</div>
                      <div className="float-text">Années</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Services Section */}
        <section
          className={`main-services ${visibleSections.has('main-services') ? 'visible' : ''}`}
          data-section="main-services"
        >
          <div className="services-container">
            <div className="section-header">
              <div className="section-badge">
                <span className="badge-dot"></span>
                <span>Nos Services Principaux</span>
              </div>
              <h2 className="section-title">
                Des services conçus pour <br />
                <span className="highlight-text">Votre Croissance</span>
              </h2>
              <p className="section-subtitle">
                Découvrez notre gamme complète de services de formation professionnelle
                adaptés à tous vos besoins de développement des compétences.
              </p>
            </div>

            <div className="services-grid">
              {mainServices.map((service, index) => {
                const Icon = service.icon;
                return (
                  <div
                    key={index}
                    className="service-card-modern"
                    style={{ '--service-color': service.color }}
                    onMouseEnter={() => setActiveService(index)}
                  >
                    <div className="service-image-wrapper">
                      <img src={service.image} alt={service.title} className="service-image" />
                      <div className="service-overlay"></div>
                      <div className="service-icon-float">
                        <Icon />
                      </div>
                    </div>

                    <div className="service-content">
                      <h3 className="service-title-modern">{service.title}</h3>
                      <p className="service-description-modern">{service.description}</p>

                      <ul className="service-features-modern">
                        {service.features.map((feature, idx) => (
                          <li key={idx}>
                            <FaCheckCircle />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {/* <button className="service-btn-modern">
                        En Savoir Plus
                        <FaArrowRight />
                      </button> */}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section
          className={`process-section ${visibleSections.has('process') ? 'visible' : ''}`}
          data-section="process"
        >
          <div className="services-container">
            <div className="section-header">
              <div className="section-badge centered">
                <span className="badge-dot"></span>
                <span>Notre processus</span>
              </div>
              <h2 className="section-title centered">
                Comment nous <span className="highlight-text">travaillons</span>
              </h2>
              <p className="section-subtitle centered">
                Un processus simple et efficace en 4 étapes pour vous accompagner vers la réussite
              </p>
            </div>

            <div className="process-grid">
              {processSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={index} className="process-card">
                    <div className="process-number">{step.number}</div>
                    <div className="process-icon">
                      <Icon />
                    </div>
                    <h3 className="process-title">{step.title}</h3>
                    <p className="process-description">{step.description}</p>
                    {index < processSteps.length - 1 && <div className="process-arrow">→</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Additional Services */}
        <section
          className={`additional-services ${visibleSections.has('additional') ? 'visible' : ''}`}
          data-section="additional"
        >
          <div className="services-container">
            <div className="section-header">
              <div className="section-badge centered">
                <span className="badge-dot"></span>
                <span>Services complémentaires</span>
              </div>
              <h2 className="section-title centered">
                Encore plus de solutions <br />
                <span className="highlight-text">Pour Votre Succès</span>
              </h2>
            </div>

            <div className="additional-grid">
              {additionalServices.map((service, index) => {
                const Icon = service.icon;
                return (
                  <div
                    key={index}
                    className="additional-card"
                    style={{ '--card-color': service.color }}
                  >
                    <div className="additional-icon">
                      <Icon />
                    </div>
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>
                    {/* <button className="additional-link">
                      Découvrir <FaArrowRight />
                    </button> */}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section
          className={`testimonials-section ${visibleSections.has('testimonials') ? 'visible' : ''}`}
          data-section="testimonials"
        >
          <div className="services-container">
            <div className="section-header">
              <div className="section-badge centered">
                <span className="badge-dot"></span>
                <span>Témoignages clients</span>
              </div>
              <h2 className="section-title centered">
                Ce que disent nos <span className="highlight-text">clients</span>
              </h2>
            </div>

            <div className="testimonials-grid">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="testimonial-card-modern">
                  <div className="testimonial-quote">"</div>
                  <p className="testimonial-text">{testimonial.text}</p>
                  <div className="testimonial-rating">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <FaStar key={i} />
                    ))}
                  </div>
                  <div className="testimonial-author">
                    <img src={testimonial.avatar} alt={testimonial.name} />
                    <div className="author-info">
                      <div className="author-name">{testimonial.name}</div>
                      <div className="author-role">{testimonial.role}</div>
                      <div className="author-company">{testimonial.company}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section
          className={`faq-section ${visibleSections.has('faq') ? 'visible' : ''}`}
          data-section="faq"
        >
          <div className="services-container">
            <div className="section-header">
              <div className="section-badge centered">
                <span className="badge-dot"></span>
                <span>Questions fréquentes</span>
              </div>
              <h2 className="section-title centered">
                Des Questions ? <br />
                <span className="highlight-text">Nous avons les réponses</span>
              </h2>
            </div>

            <div className="faq-container">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className={`faq-item ${activeAccordion === index ? 'active' : ''}`}
                  onClick={() => setActiveAccordion(activeAccordion === index ? null : index)}
                >
                  <div className="faq-question">
                    <h3>{faq.question}</h3>
                    <div className="faq-icon">{activeAccordion === index ? '−' : '+'}</div>
                  </div>
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Final */}
        {/* <section className="cta-final visible">
          <div className="services-container">
            <div className="cta-content-modern">
              <div className="cta-icon-large">
                <FaRocket />
              </div>
              <h2>Prêt à Transformer Votre Avenir ?</h2>
              <p>Rejoignez plus de 10 000 professionnels qui ont choisi CINEF pour leur développement</p>
              <div className="cta-buttons-modern">
                <button className="cta-btn-primary">
                  Commencer Maintenant
                  <FaArrowRight />
                </button>
                <button className="cta-btn-secondary">
                  Parler à un Conseiller
                </button>
              </div>
            </div>
          </div>
        </section> */}

        <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .services-wrapper {
          font-family: 'Inter', sans-serif;
          
          overflow-x: hidden;
        }

        .services-container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 24px;
        }

        /* Animations au Scroll */
        section {
          opacity: 0;
          transform: translateY(50px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }

        section.visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* Hero Section - Toujours visible */
        .services-hero {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }

        /* Hero Section */
        .services-hero {
          min-height: 90vh;
          display: flex;
          align-items: center;
          background: linear-gradient(135deg, #fff5f0 0%, #ffffff 50%, #f0f9ff 100%);
          padding: 120px 0 80px;
          position: relative;
          overflow: hidden;
        }

        .hero-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 0;
        }

        .hero-shape {
          position: absolute;
          border-radius: 50%;
          opacity: 0.6;
          animation: floatShape 20s ease-in-out infinite;
        }

        .shape-1 {
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(253, 126, 20, 0.08) 0%, transparent 70%);
          top: -10%;
          right: -10%;
        }

        .shape-2 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(25, 135, 84, 0.08) 0%, transparent 70%);
          bottom: -5%;
          left: -5%;
          animation-delay: -5s;
        }

        .shape-3 {
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, rgba(139, 92, 246, 0.08) 0%, transparent 70%);
          top: 50%;
          left: 50%;
          animation-delay: -10s;
        }

        @keyframes floatShape {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -30px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }

        .hero-content {
          display: grid;
          grid-template-columns: 1.1fr 0.9fr;
          gap: 60px;
          align-items: center;
          position: relative;
          z-index: 1;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: white;
          padding: 10px 24px;
          border-radius: 50px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          margin-bottom: 24px;
          animation: fadeInDown 0.8s ease-out;
        }

        .badge-icon-rotate {
          color: #fd7e14;
          font-size: 18px;
          animation: rotate 3s linear infinite;
        }

        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .hero-badge span {
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
        }

        .hero-title {
          font-size: 58px;
          font-weight: 900;
          line-height: 1.1;
          color: #111827;
          margin-bottom: 24px;
          animation: fadeInUp 0.8s ease-out 0.2s backwards;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .gradient-text {
          background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-description {
          font-size: 18px;
          line-height: 1.7;
          color: #6b7280;
          margin-bottom: 32px;
          animation: fadeInUp 0.8s ease-out 0.4s backwards;
        }

        .hero-buttons {
          display: flex;
          gap: 16px;
          margin-bottom: 48px;
          animation: fadeInUp 0.8s ease-out 0.6s backwards;
        }

        .btn-primary-hero, .btn-secondary-hero {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 16px 32px;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          border: none;
          font-family: inherit;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .btn-primary-hero {
          background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
          color: white;
          box-shadow: 0 10px 30px rgba(253, 126, 20, 0.3);
        }

        .btn-primary-hero:hover {
          transform: translateY(-4px);
          box-shadow: 0 15px 40px rgba(253, 126, 20, 0.4);
        }

        .btn-secondary-hero {
          background: white;
          color: #fd7e14;
          border: 2px solid #fd7e14;
        }

        .btn-secondary-hero:hover {
          background: #fd7e14;
          color: white;
          transform: translateY(-4px);
        }

        .hero-stats {
          display: flex;
          gap: 48px;
          animation: fadeInUp 0.8s ease-out 0.8s backwards;
        }

        .hero-stat {
          text-align: center;
        }

        .stat-value {
          font-size: 36px;
          font-weight: 900;
          background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 8px;
        }

        .stat-label {
          font-size: 14px;
          color: #6b7280;
          font-weight: 600;
        }

        .hero-right {
          animation: fadeInRight 0.8s ease-out 0.3s backwards;
        }

        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .hero-image-container {
          position: relative;
        }

        .hero-main-image {
          width: 100%;
          border-radius: 24px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }

        .floating-card {
          position: absolute;
          background: white;
          padding: 20px 24px;
          border-radius: 16px;
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
          display: flex;
          align-items: center;
          gap: 16px;
          animation: floatCard 3s ease-in-out infinite;
        }

        @keyframes floatCard {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
          /* Suite du CSS pour OurServices.jsx */

.card-1 {
  top: 10%;
  left: -10%;
  background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
  color: white;
}

.card-2 {
  bottom: 15%;
  right: -10%;
}

.float-icon {
  font-size: 28px;
  color: inherit;
}

.card-2 .float-icon {
  color: #fd7e14;
}

.float-number {
  font-size: 24px;
  font-weight: 900;
  line-height: 1;
}

.float-text {
  font-size: 13px;
  opacity: 0.9;
}

/* Main Services Section */
.main-services {
  padding: 100px 0;
  background: white;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #fff5f0 0%, #fef3c7 100%);
  padding: 8px 20px;
  border-radius: 50px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.section-badge.centered {
  margin: 0 auto 20px;
}

.badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fd7e14, #dc3545);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.3); opacity: 0.7; }
}

.section-badge span:last-child {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-title {
  font-size: 48px;
  font-weight: 900;
  color: #111827;
  margin-bottom: 16px;
  line-height: 1.2;
}

.section-title.centered {
  text-align: center;
}

.highlight-text {
  background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.section-subtitle {
  font-size: 18px;
  color: #6b7280;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.7;
}

.section-subtitle.centered {
  text-align: center;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;
}

.service-card-modern {
  background: white;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid transparent;
}

.service-card-modern:hover {
  transform: translateY(-12px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  border-color: var(--service-color);
}

.service-image-wrapper {
  position: relative;
  height: 240px;
  overflow: hidden;
}

.service-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s ease;
}

.service-card-modern:hover .service-image {
  transform: scale(1.1);
}

.service-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.7) 100%);
}

.service-icon-float {
  position: absolute;
  bottom: 20px;
  right: 20px;
  width: 60px;
  height: 60px;
  background: var(--service-color);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 28px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;
}

.service-card-modern:hover .service-icon-float {
  transform: scale(1.1) rotate(-5deg);
}

.service-content {
  padding: 32px;
}

.service-title-modern {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 12px;
}

.service-description-modern {
  font-size: 15px;
  color: #6b7280;
  line-height: 1.7;
  margin-bottom: 20px;
}

.service-features-modern {
  list-style: none;
  margin-bottom: 24px;
}

.service-features-modern li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #4b5563;
  margin-bottom: 10px;
}

.service-features-modern li svg {
  color: var(--service-color);
  font-size: 16px;
  flex-shrink: 0;
}

.service-btn-modern {
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  color: var(--service-color);
  border: 2px solid var(--service-color);
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.service-btn-modern:hover {
  background: var(--service-color);
  color: white;
  transform: translateX(5px);
}

/* Process Section */
.process-section {
  padding: 100px 0;
  
}

.process-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
  position: relative;
}

.process-card {
  background: white;
  padding: 40px 32px;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: all 0.4s ease;
  position: relative;
}

.process-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
}

.process-number {
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  font-weight: 900;
  box-shadow: 0 8px 20px rgba(253, 126, 20, 0.3);
}

.process-icon {
  width: 70px;
  height: 70px;
  margin: 0 auto 24px;
  background: linear-gradient(135deg, #f0f9ff 0%, #dbeafe 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fd7e14;
  font-size: 32px;
  transition: all 0.3s ease;
}

.process-card:hover .process-icon {
  transform: scale(1.1) rotate(-5deg);
}

.process-title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 12px;
}

.process-description {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.6;
}

.process-arrow {
  position: absolute;
  top: 50%;
  right: -45px;
  font-size: 32px;
  color: #e5e7eb;
  z-index: 1;
}

/* Additional Services */
.additional-services {
  padding: 100px 0;
  background: white;
}

.additional-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
}

.additional-card {
  background: #fafafa;
  padding: 40px 32px;
  border-radius: 20px;
  transition: all 0.4s ease;
  border: 2px solid transparent;
}

.additional-card:hover {
  background: white;
  border-color: var(--card-color);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  transform: translateY(-8px);
}

.additional-icon {
  width: 70px;
  height: 70px;
  border-radius: 16px;
  background: var(--card-color);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  color: white;
  font-size: 32px;
  transition: all 0.3s ease;
}

.additional-card:hover .additional-icon {
  transform: scale(1.1) rotate(-10deg);
}

.additional-card h3 {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 12px;
}

.additional-card p {
  font-size: 15px;
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 20px;
}

.additional-link {
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  color: var(--card-color);
  border: none;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  transition: gap 0.3s ease;
  font-family: inherit;
}

.additional-link:hover {
  gap: 12px;
}

/* Testimonials */
.testimonials-section {
  padding: 100px 0;
  
}

.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
}

.testimonial-card-modern {
  background: white;
  padding: 40px;
  border-radius: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: all 0.4s ease;
  position: relative;
}

.testimonial-card-modern:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
}

.testimonial-quote {
  font-size: 80px;
  color: #fd7e14;
  opacity: 0.2;
  line-height: 1;
  margin-bottom: -30px;
}

.testimonial-text {
  font-size: 16px;
  color: #4b5563;
  line-height: 1.7;
  margin-bottom: 24px;
  font-style: italic;
}

.testimonial-rating {
  display: flex;
  gap: 6px;
  margin-bottom: 24px;
  color: #fbbf24;
  font-size: 18px;
}

.testimonial-author {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-top: 24px;
  border-top: 2px solid #f3f4f6;
}

.testimonial-author img {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #fd7e14;
}

.author-info {
  flex: 1;
}

.author-name {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 4px;
}

.author-role {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 2px;
}

.author-company {
  font-size: 13px;
  color: #fd7e14;
  font-weight: 600;
}

/* FAQ Section */
.faq-section {
  padding: 100px 0;
  background: white;
}

.faq-container {
  max-width: 900px;
  margin: 0 auto;
}

.faq-item {
  background: white;
  border: 2px solid #f3f4f6;
  border-radius: 16px;
  margin-bottom: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
}

.faq-item:hover {
  border-color: #fd7e14;
}

.faq-item.active {
  border-color: #fd7e14;
  box-shadow: 0 8px 24px rgba(253, 126, 20, 0.15);
}

.faq-question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
}

.faq-question h3 {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  flex: 1;
}

.faq-icon {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
  transition: transform 0.3s ease;
}

.faq-item.active .faq-icon {
  transform: rotate(180deg);
}

.faq-answer {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.4s ease, padding 0.4s ease;
}

.faq-item.active .faq-answer {
  max-height: 300px;
  padding: 0 32px 24px;
}

.faq-answer p {
  font-size: 15px;
  color: #6b7280;
  line-height: 1.7;
}

/* CTA Final */
.cta-final {
  padding: 120px 0;
  background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
  position: relative;
  overflow: hidden;
}

.cta-final::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -20%;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  border-radius: 50%;
  animation: floatShape 15s ease-in-out infinite;
}

.cta-content-modern {
  text-align: center;
  position: relative;
  z-index: 1;
}

.cta-icon-large {
  width: 100px;
  height: 100px;
  margin: 0 auto 32px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 48px;
  animation: bounceIn 1s ease-out;
}

@keyframes bounceIn {
  0% { transform: scale(0); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

.cta-content-modern h2 {
  font-size: 56px;
  font-weight: 900;
  color: white;
  margin-bottom: 16px;
  line-height: 1.1;
}

.cta-content-modern p {
  font-size: 20px;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 40px;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
}

.cta-buttons-modern {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

.cta-btn-primary, .cta-btn-secondary {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 40px;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  border: none;
}

.cta-btn-primary {
  background: white;
  color: #fd7e14;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.cta-btn-primary:hover {
  transform: translateY(-5px);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.3);
}

.cta-btn-secondary {
  background: transparent;
  color: white;
  border: 2px solid white;
}

.cta-btn-secondary:hover {
  background: white;
  color: #fd7e14;
  transform: translateY(-5px);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .hero-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .hero-title {
    font-size: 48px;
  }

  .services-grid {
    grid-template-columns: 1fr;
  }

  .process-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .process-arrow {
    display: none;
  }

  .additional-grid, .testimonials-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .section-title, .cta-content-modern h2 {
    font-size: 42px;
  }
}

@media (max-width: 768px) {
  .services-hero {
    padding: 100px 0 60px;
  }

  .hero-title {
    font-size: 36px;
  }

  .hero-stats {
    gap: 24px;
  }

  .stat-value {
    font-size: 28px;
  }

  .floating-card {
    display: none;
  }

  .process-grid, .additional-grid, .testimonials-grid {
    grid-template-columns: 1fr;
  }

  .section-title, .cta-content-modern h2 {
    font-size: 32px;
  }

  .cta-content-modern p {
    font-size: 18px;
  }

  .cta-buttons-modern {
    flex-direction: column;
  }

  .cta-btn-primary, .cta-btn-secondary {
    width: 100%;
    justify-content: center;
  }

  .hero-buttons {
    flex-direction: column;
  }

  .btn-primary-hero, .btn-secondary-hero {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 640px) {
  .services-container {
    padding: 0 16px;
  }

  .hero-title {
    font-size: 28px;
  }

  .section-title {
    font-size: 28px;
  }

  .service-image-wrapper {
    height: 200px;
  }

  .service-content {
    padding: 24px;
  }
}
      `}</style>
      </div>
      <Footer />
    </>
  );
};
export default OurServices;